/**
 * Per-user localStorage cache for the analytics pages.
 *
 * The dashboards cached their last response under a fixed key, so on a shared
 * machine the next person to log in saw the previous user's figures (and a
 * warehouse-scoped user could see all-warehouse numbers) until the refetch
 * finished (policy C16). Keys are now scoped to the logged-in user and the
 * whole set is wiped on logout; the "instant display from cache" behaviour is
 * unchanged for the same user.
 *
 * Drop-in for the localStorage calls the pages already make: getItem returns the
 * stored string (or null), setItem takes a string.
 */
const PREFIX = 'erp_analytics::';

// Keys the pages used before scoping — swept once on logout/clearAll so stale
// unscoped copies cannot linger.
const LEGACY_PREFIXES = [
  'dashboard_data_', 'salesAnalytics_', 'financialAnalytics_',
  'customerAnalytics_', 'inventoryAnalytics_',
  'inquiryAnalytics_', 'purchaseAnalytics_', 'deliveryAnalytics_',
];

function currentUserId() {
  try {
    const raw = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
    const user = raw ? JSON.parse(raw) : null;
    return user?.id ?? user?.user_id ?? 'anon';
  } catch (e) {
    return 'anon';
  }
}

const scoped = (key) => `${PREFIX}${currentUserId()}::${key}`;

const analyticsCache = {
  getItem(key) {
    try { return localStorage.getItem(scoped(key)); } catch (e) { return null; }
  },
  setItem(key, value) {
    try { localStorage.setItem(scoped(key), value); } catch (e) { /* quota / private mode */ }
  },
  removeItem(key) {
    try { localStorage.removeItem(scoped(key)); } catch (e) { /* ignore */ }
  },
  /** Remove every analytics cache entry (all users) — called on logout. */
  clearAll() {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith(PREFIX) || LEGACY_PREFIXES.some((p) => k.startsWith(p))) {
          localStorage.removeItem(k);
        }
      }
    } catch (e) { /* ignore */ }
  },
};

export default analyticsCache;
